import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

// Reveal on scroll. Content is visible by default; the entrance animation is
// only added when JS + IntersectionObserver are available (and motion allowed),
// with a safety timer so content can never get stuck hidden.
export function Reveal({ children, className = "", delay = 0, as: Tag = "div", ...rest }) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce || typeof IntersectionObserver === "undefined") return;
    el.classList.add("anim");
    const show = () => el.classList.add("in");
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) { show(); io.disconnect(); } }),
      { rootMargin: "0px 0px -5% 0px", threshold: 0.01 }
    );
    io.observe(el);
    const fallback = setTimeout(show, 900); // never stay blank
    return () => { io.disconnect(); clearTimeout(fallback); };
  }, []);
  return (
    <Tag ref={ref} className={`reveal ${className}`} style={{ transitionDelay: `${delay}ms` }} {...rest}>
      {children}
    </Tag>
  );
}

// Split text — letters rise in, staggered.
export function SplitText({ text, className = "", style, stagger = 0.04, delay = 0 }) {
  const chars = String(text).split("");
  return (
    <span className={className} style={{ whiteSpace: "nowrap", ...style }} aria-label={text}>
      {chars.map((c, i) => (
        <span className="clip" key={i} aria-hidden>
          <motion.span
            style={{ display: "inline-block" }}
            initial={{ y: "110%" }}
            animate={{ y: 0 }}
            transition={{ duration: 0.8, ease: EASE, delay: delay + i * stagger }}
          >
            {c === " " ? "\u00A0" : c}
          </motion.span>
        </span>
      ))}
    </span>
  );
}

// Rotating discipline word. A hidden in-flow sizer reserves space; the active
// word is absolutely positioned and animated in/out with AnimatePresence so it
// always resolves to opacity 1 / y 0 (never stuck at the initial offset).
export function RotatingText({ words, className = "", style, interval = 2200 }) {
  const [i, setI] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setI((v) => (v + 1) % words.length), interval);
    return () => clearInterval(t);
  }, [words.length, interval]);
  const word = words[i];
  return (
    <span
      className={className}
      style={{ position: "relative", display: "inline-block", overflow: "hidden", verticalAlign: "bottom", ...style }}
    >
      <span style={{ visibility: "hidden", whiteSpace: "nowrap" }} aria-hidden>{word}</span>
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={word}
          style={{ display: "inline-block", position: "absolute", left: 0, top: 0, whiteSpace: "nowrap" }}
          initial={{ y: "100%", opacity: 0 }}
          animate={{ y: "0%", opacity: 1 }}
          exit={{ y: "-100%", opacity: 0 }}
          transition={{ duration: 0.5, ease: EASE }}
        >
          {word}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}

// Count up when scrolled into view.
export function CountUp({ to, suffix = "", prefix = "", className = "", duration = 1400 }) {
  const ref = useRef(null);
  const [val, setVal] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) {
        io.disconnect();
        const start = performance.now();
        const step = (now) => {
          const t = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          setVal(Math.round(eased * to));
          if (t < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
      }
    }, { threshold: 0.4 });
    io.observe(el);
    return () => io.disconnect();
  }, [to, duration]);
  return <span ref={ref} className={className}>{prefix}{val}{suffix}</span>;
}

// Magnetic hover pull.
export function Magnetic({ children, strength = 0.35, className = "", ...rest }) {
  const ref = useRef(null);
  const onMove = (e) => {
    const el = ref.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - (r.left + r.width / 2)) * strength;
    const y = (e.clientY - (r.top + r.height / 2)) * strength;
    el.style.transform = `translate(${x}px, ${y}px)`;
  };
  const reset = () => { if (ref.current) ref.current.style.transform = "translate(0,0)"; };
  return (
    <span
      ref={ref}
      onMouseMove={onMove}
      onMouseLeave={reset}
      className={className}
      style={{ display: "inline-block", transition: "transform .3s cubic-bezier(.22,1,.36,1)" }}
      {...rest}
    >
      {children}
    </span>
  );
}
